import React from 'react'
import Section from '../components/layout/Section'
import Seo from '../components/seo/Seo'
import { SHAWN, MERRITT } from '../seo/schema'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'

const About: React.FC = () => {
  const crumbs = useBreadcrumbs([{ label: 'About', path: '/about' }])
  return (
    <div>
      <Seo
        title="About Shawn McDonnell & Merritt Cooch | Lewes DE REALTORS®"
        description="Meet Shawn McDonnell (Associate Broker) and Merritt Cooch — a husband-and-wife team at Jack Lingo REALTOR® serving Lewes, Rehoboth and the Delaware beaches."
        path="/about"
        jsonLd={[SHAWN, MERRITT, crumbs.jsonLd]}
      />
      {/* Hero */}
      <section className="overflow-hidden bg-sand pt-24">
        <div className="mx-auto grid min-h-[620px] max-w-7xl grid-cols-1 items-center gap-10 px-6 py-12 lg:grid-cols-[1.05fr_0.95fr] lg:py-16">
          <div className="order-2 lg:order-1">
            {crumbs.node}
            <p className="font-sans text-xs font-medium uppercase tracking-[0.28em] text-primary-navy">Our Story</p>
            <h1 className="mt-4 font-sans text-[clamp(3.2rem,8vw,7.5rem)] font-medium leading-[0.82] tracking-[-0.065em] text-primary-ink">
              Merritt <span className="font-light text-accent-gold">&amp;</span><br />Shawn
            </h1>
            <p className="mt-8 max-w-xl text-lg font-light leading-[1.8] text-primary-ink/75 md:text-xl">
              Two complementary perspectives, one deeply personal approach to Coastal Delaware real estate.
              Merritt brings generations of local knowledge and investor experience; Shawn brings a builder&apos;s
              eye, market strategy, and calm negotiation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="/contact" className="inline-flex h-11 items-center rounded-full bg-accent-gold px-6 font-sans text-xs font-medium uppercase tracking-[0.16em] text-primary-ink transition-colors hover:bg-primary-navy hover:text-white">Book a Consultation</a>
              <a href="https://www.instagram.com/merrittandshawn/" target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center rounded-full border border-primary-ink/20 bg-white/70 px-6 font-sans text-xs font-medium uppercase tracking-[0.16em] text-primary-ink transition-colors hover:border-primary-navy hover:text-primary-navy">Follow @merrittandshawn</a>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <img
              src="/media/team/merritt-shawn-coastal.webp"
              alt="Merritt Cooch and Shawn McDonnell on the beach in Rehoboth Beach"
              width={1600}
              height={1586}
              className="aspect-square w-full rounded-t-[10rem] rounded-b-[2rem] border-8 border-white object-cover object-top shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Team Bios */}
      <Section className="bg-sand">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Shawn McDonnell */}
          <div className="flex flex-col items-stretch text-left">
            <div className="relative group">
              <div className="absolute inset-0 rounded-t-[8rem] rounded-b-3xl bg-accent-gold/20 blur-2xl transform translate-y-4 group-hover:translate-y-2 transition-transform duration-500"></div>
              <img
                src="/media/team/shawn-mcdonnell-800.jpg"
                alt="Shawn McDonnell portrait"
                className="relative aspect-[4/5] w-full max-w-md rounded-t-[8rem] rounded-b-3xl object-cover object-top shadow-2xl border-4 border-white group-hover:border-accent-gold/50 transition-colors duration-500"
              />
            </div>
            <div className="mt-10">
              <h2 className="font-sans text-5xl font-medium tracking-[-0.045em] mb-2 text-primary-ink">Shawn McDonnell</h2>
              <p className="text-lg text-accent-gold font-medium mb-6">Associate Broker</p>
              <p className="text-lg text-neutral-slate mb-4 leading-relaxed">
                A Philadelphia native and Rutgers University graduate, Shawn spent his college summers working in Rehoboth Beach before moving to the beach full-time in 2009. He ran a historic home restoration company with his father — restoring homes from Old New Castle to Rehoboth Beach — and he inspects every property with a builder's eye.
              </p>
              <p className="text-lg text-neutral-slate mb-6 leading-relaxed">
                Shawn joined his wife Merritt on the Carrie Lingo Team in 2017 and has been one of the top-selling REALTORS® in the State of Delaware since. He holds the NAR Resort and Second Home Property Specialist certification.
              </p>
              <div className="space-y-3 rounded-2xl border border-primary-ink/10 bg-white/80 p-6 text-left shadow-sm">
                <div className="flex items-start">
                  <span className="font-semibold w-20 text-primary-navy">Office:</span>
                  <span className="text-neutral-slate">302-645-2207</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold w-20 text-primary-navy">Cell:</span>
                  <span className="text-neutral-slate">302-542-8591</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold w-20 text-primary-navy">Email:</span>
                  <span className="text-neutral-slate">shawn@jacklingo.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Merritt Cooch */}
          <div className="flex flex-col items-stretch text-left">
            <div className="relative group">
              <div className="absolute inset-0 rounded-t-[8rem] rounded-b-3xl bg-accent-gold/20 blur-2xl transform translate-y-4 group-hover:translate-y-2 transition-transform duration-500"></div>
              <img
                src="/media/team/merritt-cooch-800.jpg"
                alt="Merritt Cooch portrait"
                className="relative aspect-[4/5] w-full max-w-md rounded-t-[8rem] rounded-b-3xl object-cover object-top shadow-2xl border-4 border-white group-hover:border-accent-gold/50 transition-colors duration-500"
              />
            </div>
            <div className="mt-10">
              <h2 className="font-sans text-5xl font-medium tracking-[-0.045em] mb-2 text-primary-ink">Merritt Cooch</h2>
              <p className="text-lg text-accent-gold font-medium mb-6">REALTOR®</p>
              <p className="text-lg text-neutral-slate mb-4 leading-relaxed">
                An 11th-generation Delaware native, Merritt's family history traces back to Cooch's Bridge — the only Revolutionary War battle fought in Delaware happened on her family's land. Originally from New Castle, she earned her B.S. in Health Behavior Science from the University of Delaware and built her career in Rehoboth Beach property management.
              </p>
              <p className="text-lg text-neutral-slate mb-6 leading-relaxed">
                A Zillow Premier Agent and active real-estate investor, Merritt joined the Carrie Lingo Team in 2016. In its “Faces of Delaware 2025” profile,{' '}
                <a href="https://delawaretoday.com/partner/faces-of-delaware-today-2025/merritt-cooch/" target="_blank" rel="noopener noreferrer" className="font-medium text-primary-navy underline decoration-accent-gold/60 underline-offset-4 hover:text-accent-gold">Delaware Today</a>{' '}
                recognized her as “The Face of the Next Generation of Delaware Beach Real Estate,” highlighting her fresh perspective, local knowledge, network, and warm personality.
              </p>
              <div className="space-y-3 rounded-2xl border border-primary-ink/10 bg-white/80 p-6 text-left shadow-sm">
                <div className="flex items-start">
                  <span className="font-semibold w-20 text-primary-navy">Cell:</span>
                  <span className="text-neutral-slate">302-562-7954</span>
                </div>
                <div className="flex items-start">
                  <span className="font-semibold w-20 text-primary-navy">Email:</span>
                  <span className="text-neutral-slate">merritt@jacklingo.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section className="bg-primary-mist">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="overflow-hidden rounded-t-[7rem] rounded-b-3xl bg-white shadow-xl">
            <img
              src="/media/team/merritt-shawn-coastal-portrait.webp"
              alt="Merritt and Shawn standing together on Rehoboth Beach"
              width={1000}
              height={1300}
              loading="lazy"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-[0.28em] text-primary-navy">Real Coastal Experience</p>
            <h2 className="mt-4 font-sans text-[clamp(2.5rem,5vw,5rem)] font-medium leading-[0.95] tracking-[-0.05em] text-primary-ink">
              This is home,<br />not a market we visit.
            </h2>
            <div className="mt-8 space-y-5 text-lg font-light leading-[1.8] text-primary-ink/75">
              <p>
                Merritt and Shawn live, work, invest, renovate, and raise their family at the Delaware beaches.
                Their advice comes from the same decisions their clients face: where to buy, what to improve,
                how a property will live year-round, and what makes a coastal home hold its value.
              </p>
              <p>
                Adelmare, Merritt&apos;s family&apos;s 1929 Rehoboth Beach house, has been part of her story for
                generations and was the setting for their wedding in 2015. Sharing that home with guests for
                more than a decade gives them practical insight into historic properties, hospitality, and
                the realities of owning near the beach.
              </p>
            </div>
            <a href="https://airbnb.com/h/adelmare" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex h-11 items-center rounded-full border border-primary-ink/20 bg-white/80 px-6 font-sans text-xs font-medium uppercase tracking-[0.16em] text-primary-ink transition-colors hover:border-primary-navy hover:text-primary-navy">
              Discover the Adelmare Story
            </a>
          </div>
        </div>
      </Section>

      {/* Brokerage Credibility Strip */}
      <Section className="bg-neutral-charcoal text-center py-20">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-display uppercase font-bold mb-4 text-white">Jack Lingo REALTOR®</h2>
            <p className="text-lg md:text-xl text-neutral-slate leading-relaxed">
              Founded in <span className="text-accent-gold font-semibold">1974</span>, Jack Lingo REALTOR® is the most trusted name at the Delaware beaches. As an Associate Broker and a REALTOR® on the Carrie Lingo Team, Shawn and Merritt represent the highest standards of professionalism and client service.
            </p>
          </div>
          <div className="border-t border-neutral-slate/50 pt-8">
            <p className="text-neutral-slate/80">
              Office: <span className="text-white">1240 Kings Highway, Lewes, DE 19958</span>
            </p>
          </div>
        </div>
      </Section>
    </div>
  )
}

export default About
